import React, { forwardRef } from 'react'
import { useHref, useNavigate } from 'react-router-dom'

import Box, { type BoxProps } from '../box'
import { createView, VIEW_BASE } from '../_support'

/* -----------------------------------------------------------------------------
 * BoxLink
 *
 * A Box that navigates to `path`. The outer element used to be a bare
 * Pressable around the styled Box; it is now an <a href>, so the link opens in
 * a new tab with a modifier key or the middle button. The Box inside keeps
 * every style prop, as before.
 * -------------------------------------------------------------------------- */

const LinkRoot = createView({
  displayName: 'BoxLinkRoot',
  groups: {},
  tag: 'a',
  base: VIEW_BASE
})

export type BoxLinkProps = BoxProps & {
  path: string
  params?: Record<string, any>
  deactivated?: boolean
  disabled?: boolean
  children?: React.ReactNode
}

const isPlainClick = (event: React.MouseEvent) =>
  event.button === 0 &&
  !event.metaKey &&
  !event.ctrlKey &&
  !event.shiftKey &&
  !event.altKey

const BoxLink = forwardRef<HTMLElement, BoxLinkProps>(
  (
    { path, params, deactivated = false, disabled = false, children, ...rest },
    ref
  ) => {
    const navigate = useNavigate()
    const href = useHref(path)
    const active = !disabled && !deactivated

    const handleClick = (event: React.MouseEvent) => {
      if (!active) {
        event.preventDefault()
        return
      }
      if (!isPlainClick(event)) return
      event.preventDefault()
      navigate(path)
    }

    return (
      <LinkRoot
        href={active ? href : undefined}
        onClick={handleClick}
        kitLayers={{
          classes: active
            ? { cursor: 'pointer', touchAction: 'manipulation' }
            : {}
        }}
        style={{ cursor: 'pointer' }}
      >
        <Box ref={ref} {...rest}>
          {children}
        </Box>
      </LinkRoot>
    )
  }
)
BoxLink.displayName = 'BoxLink'

export default BoxLink
